import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { toast } from "@/components/toast/toast-queue";

import { useBulkCopyLinks } from "./useBulkCopyLinks";
import { useBulkDuplicateRequests } from "./useBulkDuplicateRequests";

interface UseRequestRowActionsOptions {
  onDuplicateSuccess?: () => void;
}

export function useRequestRowActions(
  options: UseRequestRowActionsOptions = {},
) {
  const navigate = useNavigate();
  const [duplicateRequestId, setDuplicateRequestId] = useState<null | number>(
    null,
  );
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const duplicateMutation = useBulkDuplicateRequests({
    onError: () => {
      toast.add({
        description: "Failed to duplicate request. Please try again.",
        timeout: 3000,
        title: "Duplicate Failed",
        type: "error",
      });
    },
    onSuccess: (data) => {
      setIsDuplicateModalOpen(false);
      const { failed, successful } = data.summary;

      if (failed > 0) {
        toast.add({
          description: "The request could not be duplicated.",
          timeout: 4000,
          title: "Duplicate Failed",
          type: "error",
        });
      } else {
        toast.add({
          description: `${successful} request duplicated successfully.`,
          timeout: 3000,
          title: "Request Duplicated",
          type: "success",
        });

        const newRequestId = data.results[0]?.duplicatedRequest?.id;

        if (typeof newRequestId === "number") {
          navigate({
            params: { requestId: newRequestId },
            to: "/requests/$requestId/edit",
          });
        }
      }
      options.onDuplicateSuccess?.();
    },
  });

  const copyLinkMutation = useBulkCopyLinks();

  const onDuplicate = (requestId: number) => {
    setDuplicateRequestId(requestId);
    setIsDuplicateModalOpen(true);
  };
  const onCopyLink = (requestId: number) =>
    copyLinkMutation.mutate([requestId]);

  const confirmDuplicate = (duplicateOptions: {
    includeAttachments: boolean;
    includeCollaborators: boolean;
  }) => {
    if (duplicateRequestId === null) return;

    duplicateMutation.mutate({
      duplicateOptions: { ...duplicateOptions, includeInternalNotes: true },
      requestIds: [duplicateRequestId],
    });
  };

  return {
    customProps: {
      onCopyLink,
      onDuplicate,
    },
    duplicateModalProps: {
      isLoading: duplicateMutation.isPending,
      isOpen: isDuplicateModalOpen,
      onConfirm: confirmDuplicate,
      onOpenChange: setIsDuplicateModalOpen,
      selectedCount: isDuplicateModalOpen ? 1 : 0,
    },
  };
}
